import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuthStore } from "../stores/authStore";
import { useFolderStore } from "../stores/folderStore";
import { useLabelStore } from "../stores/labelStore";
import { useEmailStore } from "../stores/emailStore";
import { useNotificationStore } from "../stores/useNotificationStore";
import { useApi } from "../hooks/useApi";
import MailLayout from "../layouts/MailLayout";
import ComposeModal from "../components/mail/ComposeModal";
import KeyboardShortcutsModal from "../components/mail/KeyboardShortcutsModal";
import OfflineBanner from "../components/mail/OfflineBanner";
import { useNetworkStatus } from "../hooks/useNetworkStatus";
import { saveFoldersToCache, getCachedFolders } from "../utils/offlineStorage";
import { refreshFolders } from "../utils/refreshFolders";
import { deleteEmailsWithUndo } from "../utils/deleteWithUndo";
import { normalizeFolders } from "../utils/folderHelpers";
import { showEmailNotification, playNotificationSound, updateAppBadge } from "../utils/notification";
import { sanitizeHtml } from "../utils/sanitize";
import { getApiBaseUrl } from "../utils/apiConfig";

export interface ReplyToData {
  to: string;
  cc?: string;
  bcc?: string;
  subject: string;
  body: string;
  bodyHtml?: string;
  mode?: "reply" | "forward" | "draft";
  draftId?: string;
  draftFolder?: string;
  attachments?: {
    filename: string;
    contentType: string;
    size: number;
    contentBase64?: string;
    contentId?: string;
  }[];
}

export default function MailPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token } = useAuthStore();
  const { setFolders, setActiveFolder } = useFolderStore();
  const { setLabels } = useLabelStore();
  const { get } = useApi();
  const { setInstallPrompt, soundEnabled, notificationsEnabled } = useNotificationStore();

  const [showCompose, setShowCompose] = useState(false);
  const [replyTo, setReplyTo] = useState<ReplyToData | undefined>(undefined);

  const lastSeenInboxUnreadRef = useRef<number | null>(null);
  const lastKnownEmailIdRef = useRef<string | null>(null);

  // Check URL query action
  useEffect(() => {
    if (searchParams.get("action") === "compose") {
      setShowCompose(true);
    }
  }, [searchParams]);

  // Capture PWA beforeinstallprompt event
  useEffect(() => {
    const handleBeforeInstall = (e: Event) => {
      e.preventDefault();
      setInstallPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", handleBeforeInstall);
    return () => window.removeEventListener("beforeinstallprompt", handleBeforeInstall);
  }, [setInstallPrompt]);

  // Listen for Service Worker navigation messages
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;

    const handleMessage = async (event: MessageEvent) => {
      if (event.data?.type === "NAVIGATE_EMAIL") {
        const { folderId, emailId } = event.data;
        if (folderId) {
          setActiveFolder(folderId);
        }
        if (emailId) {
          try {
            const folder = encodeURIComponent(folderId || "INBOX");
            const res = await fetch(`${getApiBaseUrl()}/mail/mails/${folder}/${emailId}`, {
              headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            if (res.ok) {
              const fullData = await res.json();
              if (fullData?.email) {
                useEmailStore.getState().setCurrentEmail(fullData.email);
              }
            }
          } catch (e) {
            console.warn("Could not fetch email from notification click:", e);
          }
        }
      }
    };

    navigator.serviceWorker.addEventListener("message", handleMessage);
    return () => navigator.serviceWorker.removeEventListener("message", handleMessage);
  }, [setActiveFolder, token]);

  const handleReply = () => {
    const email = useEmailStore.getState().currentEmail;
    if (!email) return;
    const to = email.from.email;
    setReplyTo({
      to,
      subject: email.subject.startsWith("Re:") ? email.subject : `Re: ${email.subject}`,
      body: email.body || "",
      bodyHtml: email.bodyHtml,
      mode: "reply",
    });
    setShowCompose(true);
  };

  const handleReplyAll = () => {
    const email = useEmailStore.getState().currentEmail;
    if (!email) return;
    const currentUserEmail = (useAuthStore.getState().user?.email || "").toLowerCase().trim();

    // 1. Destinatario principal: El remitente original
    const to = email.from.email;

    // 2. Destinatarios en CC: Todos los destinatarios originales ('to' y 'cc') excepto el remitente y el usuario actual
    const otherRecipients = new Set<string>();

    // De la lista 'to' original:
    if (Array.isArray(email.to)) {
      email.to.forEach((r) => {
        const addr = (r.email || (r as any).address || "").toLowerCase().trim();
        if (addr && addr !== to.toLowerCase() && addr !== currentUserEmail) {
          otherRecipients.add(addr);
        }
      });
    }

    // De la lista 'cc' original:
    if (Array.isArray(email.cc)) {
      email.cc.forEach((r) => {
        const addr = (r.email || (r as any).address || "").toLowerCase().trim();
        if (addr && addr !== to.toLowerCase() && addr !== currentUserEmail) {
          otherRecipients.add(addr);
        }
      });
    }

    const ccList = Array.from(otherRecipients).join(", ");

    setReplyTo({
      to,
      cc: ccList,
      subject: email.subject.startsWith("Re:") ? email.subject : `Re: ${email.subject}`,
      body: email.body || "",
      bodyHtml: email.bodyHtml,
      mode: "reply",
    });
    setShowCompose(true);
  };

  const handleForward = () => {
    const email = useEmailStore.getState().currentEmail;
    if (!email) return;

    const fromStr = email.from.name
      ? `${email.from.name} <${email.from.email}>`
      : email.from.email;

    const toStr = Array.isArray(email.to)
      ? email.to.map((r) => r.name ? `${r.name} <${r.email}>` : r.email).join(", ")
      : "";

    const ccStr = Array.isArray(email.cc) && email.cc.length > 0
      ? email.cc.map((r) => r.name ? `${r.name} <${r.email}>` : r.email).join(", ")
      : "";

    const dateStr = new Date(email.date).toLocaleString();

    // Header en texto plano
    const textHeader =
      `---------- Mensaje reenviado ----------\n` +
      `De: ${fromStr}\n` +
      `Fecha: ${dateStr}\n` +
      `Asunto: ${email.subject}\n` +
      `Para: ${toStr}\n` +
      (ccStr ? `Cc: ${ccStr}\n` : "") +
      `\n`;

    // Header en HTML
    const htmlHeader =
      `<div style="color:#555;border-left:1px solid #ccc;padding-left:10px;margin:12px 0;">` +
      `<b>---------- Mensaje reenviado ----------</b><br>` +
      `<b>De:</b> ${fromStr}<br>` +
      `<b>Fecha:</b> ${dateStr}<br>` +
      `<b>Asunto:</b> ${email.subject}<br>` +
      `<b>Para:</b> ${toStr}<br>` +
      (ccStr ? `<b>Cc:</b> ${ccStr}<br>` : "") +
      `</div>`;

    const body = email.body || "";
    let processedHtml = email.bodyHtml;
    if (processedHtml) {
      // Resolver imágenes inline (CID) para que se vean tal cual en el reenvío
      const sanitized = sanitizeHtml(processedHtml, {
        allowExternalImages: true,
        attachments: email.attachments,
      });
      processedHtml = sanitized.html;
    }

    setReplyTo({
      to: "",
      subject: email.subject.startsWith("Fwd:") ? email.subject : `Fwd: ${email.subject}`,
      body: textHeader + body,
      bodyHtml: processedHtml
        ? `${htmlHeader}<div class="email-forward-quote">${processedHtml}</div>`
        : `${htmlHeader}<div class="email-forward-quote">${body.replace(/\n/g, "<br>")}</div>`,
      mode: "forward",
      attachments: email.attachments?.map((a) => ({
        filename: a.filename,
        contentType: a.contentType,
        size: a.size || 0,
        contentBase64: a.contentBase64,
        contentId: a.contentId,
      })),
    });
    setShowCompose(true);
  };

  const handleEditDraft = (draftEmail: any) => {
    if (!draftEmail) return;
    const to = Array.isArray(draftEmail.to)
      ? draftEmail.to.map((t: any) => t.email || t.address || t).join(", ")
      : draftEmail.to?.email || "";
    const cc = Array.isArray(draftEmail.cc)
      ? draftEmail.cc.map((c: any) => c.email || c.address || c).join(", ")
      : "";
    const bcc = Array.isArray(draftEmail.bcc)
      ? draftEmail.bcc.map((b: any) => b.email || b.address || b).join(", ")
      : "";

    setReplyTo({
      to,
      cc,
      bcc,
      subject: draftEmail.subject === "(Sin asunto)" || draftEmail.subject === "(Borrador)" ? "" : draftEmail.subject || "",
      body: draftEmail.body || "",
      bodyHtml: draftEmail.bodyHtml,
      mode: "draft",
      draftId: draftEmail.id || draftEmail.uid,
      draftFolder: draftEmail.folderId || "Drafts",
    });
    setShowCompose(true);
  };

  const { isOnline, outboxCount, isSyncing, processOutbox } = useNetworkStatus();

  // Auto-sync outbox when returning online
  useEffect(() => {
    if (isOnline && token) {
      processOutbox(token);
    }
  }, [isOnline, token, processOutbox]);

  // Fetch initial folders & labels
  const fetchInitialData = useCallback(async () => {
    if (!token) return;
    try {
      const foldersData = await get<{
        folders: Array<{
          path: string;
          name: string;
          unread: number;
          total: number;
          flags?: string[];
        }>;
      }>("/mail/folders");

      if (foldersData?.folders && Array.isArray(foldersData.folders)) {
        const mappedFolders = normalizeFolders(foldersData.folders);
        setFolders(mappedFolders);
        saveFoldersToCache(mappedFolders);

        // Track inbox unread & update App Badge on mobile/desktop PWA icon
        const inbox = mappedFolders.find((f) => f.id.toUpperCase() === "INBOX" || f.name === "Bandeja de entrada");
        if (inbox) {
          lastSeenInboxUnreadRef.current = inbox.unreadCount;
        }
        const totalUnreadAllFolders = mappedFolders.reduce((sum, f) => sum + (f.unreadCount || 0), 0);
        updateAppBadge(totalUnreadAllFolders);
      }

      try {
        const labelsData = await get<{
          labels: Array<{ id: string; name: string; color: string }>;
        }>("/labels");
        setLabels(labelsData.labels);
      } catch {
        // Labels endpoint optional
      }
    } catch (err) {
      console.warn("Failed to fetch initial data from network, trying cache:", err);
      // Fallback a carpetas en caché IndexedDB
      try {
        const cached = await getCachedFolders();
        if (cached && cached.length > 0) {
          // getAll() de IndexedDB devuelve las carpetas ordenadas por key
          // (path alfabético): Archive, Drafts, INBOX, Junk... Hay que
          // re-normalizar o el sidebar mostraría Archivo antes que Spam.
          setFolders(normalizeFolders(cached));
        }
      } catch {
        // silent
      }
    }
  }, [token, get, setFolders, setLabels]);

  useEffect(() => {
    if (!token) {
      navigate("/login");
      return;
    }
    fetchInitialData();
    // Load quota data
    useAuthStore.getState().fetchMe();
  }, [token, navigate, fetchInitialData]);

  // Track unread counts across all linked accounts
  const accountsUnreadRef = useRef<Record<string, number>>({});
  const accountsKnownIdRef = useRef<Record<string, string>>({});

  // Background watcher for new incoming emails across ALL linked accounts (Polling every 10s)
  useEffect(() => {
    const checkAllAccounts = async () => {
      const { accounts, user } = useAuthStore.getState();
      const allAccounts = accounts && accounts.length > 0 ? accounts : (user ? [user] : []);

      for (const acc of allAccounts) {
        if (!acc.token) continue;
        try {
          const res = await fetch(`${getApiBaseUrl()}/mail/folders`, {
            headers: { Authorization: `Bearer ${acc.token}` },
          });
          if (!res.ok) continue;
          const data = await res.json();
          if (!data.folders || !Array.isArray(data.folders)) continue;

          const mappedFolders = normalizeFolders(data.folders);
          const inbox = mappedFolders.find(
            (f) => f.id.toUpperCase() === "INBOX" || f.name === "Bandeja de entrada"
          );

          if (inbox) {
            const currentUnread = inbox.unreadCount;
            const prevUnread = accountsUnreadRef.current[acc.email];

            // If unread count has increased for this account
            if (prevUnread !== undefined && currentUnread > prevUnread) {
              try {
                const mailsRes = await fetch(`${getApiBaseUrl()}/mail/mails?folder=INBOX&limit=1`, {
                  headers: { Authorization: `Bearer ${acc.token}` },
                });
                if (mailsRes.ok) {
                  const mailsData = await mailsRes.json();
                  const newestEmail = mailsData.emails?.[0];

                  if (newestEmail && String(newestEmail.uid) !== accountsKnownIdRef.current[acc.email]) {
                    accountsKnownIdRef.current[acc.email] = String(newestEmail.uid);

                    if (soundEnabled) {
                      playNotificationSound();
                    }

                    const sender = Array.isArray(newestEmail.from)
                      ? (newestEmail.from[0]?.name || newestEmail.from[0]?.address || "Remitente")
                      : (newestEmail.from?.name || newestEmail.from?.email || "Remitente");

                    const isCurrentAccount = user?.email.toLowerCase() === acc.email.toLowerCase();
                    const accountLabel = isCurrentAccount ? "" : ` [${acc.email}]`;

                    showEmailNotification({
                      title: `Nuevo correo para ${acc.email}${accountLabel}`,
                      body: `${sender}: ${newestEmail.subject || "(Sin asunto)"}`,
                      folderId: "INBOX",
                      emailId: String(newestEmail.uid),
                    });
                  }
                }
              } catch (err) {
                console.error("Error fetching newest email for multi-account notification:", err);
              }
            }

            accountsUnreadRef.current[acc.email] = currentUnread;

            // If it's the currently active account, update folder state and app badge
            if (user?.email.toLowerCase() === acc.email.toLowerCase()) {
              setFolders(mappedFolders);
              const totalUnreadAll = mappedFolders.reduce((sum, f) => sum + (f.unreadCount || 0), 0);
              updateAppBadge(totalUnreadAll);
            }
          }
        } catch {
          // Suppress errors for background checks
        }
      }
    };

    checkAllAccounts();
    const intervalId = setInterval(checkAllAccounts, 10000);
    return () => clearInterval(intervalId);
  }, [soundEnabled, notificationsEnabled, setFolders]);

  // Refresh quota periodically (every 60s)
  useEffect(() => {
    if (!token) return;
    const quotaInterval = setInterval(() => {
      useAuthStore.getState().fetchMe();
    }, 60000);
    return () => clearInterval(quotaInterval);
  }, [token]);

  const [showGlobalShortcutsModal, setShowGlobalShortcutsModal] = useState(false);
  const { del, patch } = useApi();

  // Desktop Global Keyboard Shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // No capturar atajos si el usuario está escribiendo en un input, textarea, contenteditable, modal activo o diálogo abierto
      const target = e.target as HTMLElement | null;
      if (
        !target ||
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.tagName === "SELECT" ||
        target.isContentEditable ||
        target.closest("[contenteditable]") ||
        target.closest("input, textarea, select") ||
        showCompose ||
        document.querySelector('[role="dialog"]') !== null
      ) {
        return;
      }

      // Si el usuario presiona Ctrl o Meta (Cmd en Mac), NUNCA interceptar teclas como C, V, X, A, R, F, S, etc.
      // para preservar atajos vitales del sistema (Copiar, Pegar, Cortar, Seleccionar Todo, Recargar, Buscar, Guardar).
      if (e.ctrlKey || e.metaKey || e.altKey) {
        return;
      }

      // Si el usuario tiene texto seleccionado en el visor o pantalla, no secuestrar la tecla 'C' ni teclas de acción
      const activeSelection = window.getSelection()?.toString();
      if (activeSelection && activeSelection.length > 0) {
        return;
      }

      const {
        emails,
        currentEmail,
        setCurrentEmail,
        removeEmails,
        markAsRead,
        markAsUnread,
        toggleFlag,
        toggleSelectEmail,
        selectAllEmails,
      } = useEmailStore.getState();

      const activeFolder = useFolderStore.getState().activeFolderId || "INBOX";

      // C -> Redactar
      if (e.key === "c" || e.key === "C") {
        e.preventDefault();
        setReplyTo(undefined);
        setShowCompose(true);
        return;
      }

      // ? -> Abrir ayuda de atajos
      if (e.key === "?") {
        e.preventDefault();
        setShowGlobalShortcutsModal((prev) => !prev);
        return;
      }

      // / -> Enfocar campo de búsqueda
      if (e.key === "/") {
        e.preventDefault();
        const searchInput = document.querySelector('input[placeholder*="Buscar"]') as HTMLInputElement;
        if (searchInput) {
          searchInput.focus();
          searchInput.select();
        }
        return;
      }

      // Escape -> Cerrar visor o deseleccionar correo
      if (e.key === "Escape") {
        if (showGlobalShortcutsModal) {
          setShowGlobalShortcutsModal(false);
        } else if (currentEmail) {
          setCurrentEmail(null);
        }
        return;
      }

      // J o Flecha Abajo -> Siguiente correo
      if (e.key === "j" || e.key === "J" || e.key === "ArrowDown") {
        e.preventDefault();
        if (emails.length === 0) return;
        const currentIndex = emails.findIndex((m) => m.id === currentEmail?.id);
        const nextIndex = currentIndex < emails.length - 1 ? currentIndex + 1 : 0;
        setCurrentEmail(emails[nextIndex]);
        return;
      }

      // K o Flecha Arriba -> Correo anterior
      if (e.key === "k" || e.key === "K" || e.key === "ArrowUp") {
        e.preventDefault();
        if (emails.length === 0) return;
        const currentIndex = emails.findIndex((m) => m.id === currentEmail?.id);
        const prevIndex = currentIndex > 0 ? currentIndex - 1 : emails.length - 1;
        setCurrentEmail(emails[prevIndex]);
        return;
      }

      // R -> Responder / Shift+R -> Responder a todos
      if (e.key === "r" || e.key === "R") {
        if (currentEmail) {
          e.preventDefault();
          if (e.shiftKey) {
            handleReplyAll();
          } else {
            handleReply();
          }
        }
        return;
      }

      // F -> Reenviar
      if (e.key === "f" || e.key === "F") {
        if (currentEmail) {
          e.preventDefault();
          handleForward();
        }
        return;
      }

      // E o Supr / Delete -> Mover a papelera
      if (e.key === "e" || e.key === "E" || e.key === "Delete" || e.key === "Backspace") {
        if (currentEmail) {
          e.preventDefault();
          const emailToDelete = currentEmail;
          deleteEmailsWithUndo(
            [emailToDelete],
            () =>
              del(`/mail/mails/${encodeURIComponent(emailToDelete.folderId || activeFolder)}/${emailToDelete.id}`).catch((err) => {
                console.error("Error deleting email via shortcut:", err);
              })
          );
        }
        return;
      }

      // S -> Alternar Estrella (Flag)
      if (e.key === "s" || e.key === "S") {
        if (currentEmail) {
          e.preventDefault();
          toggleFlag(currentEmail.id);
          patch(`/mail/mails/${encodeURIComponent(currentEmail.folderId || activeFolder)}/${currentEmail.id}/flags`, {
            flagged: !currentEmail.isFlagged,
          }).catch((err) => {
            console.error("Error flagging email via shortcut:", err);
          });
        }
        return;
      }

      // U -> Alternar leído / no leído
      if (e.key === "u" || e.key === "U") {
        if (currentEmail) {
          e.preventDefault();
          const newRead = !currentEmail.isRead;
          if (newRead) markAsRead([currentEmail.id]);
          else markAsUnread([currentEmail.id]);
          patch(`/mail/mails/${encodeURIComponent(currentEmail.folderId || activeFolder)}/${currentEmail.id}/flags`, {
            read: newRead,
          }).catch((err) => {
            console.error("Error updating read status via shortcut:", err);
          });
        }
        return;
      }

      // X -> Seleccionar casilla
      if (e.key === "x" || e.key === "X") {
        if (currentEmail) {
          e.preventDefault();
          toggleSelectEmail(currentEmail.id);
        }
        return;
      }

      // Shift + A -> Seleccionar todos los correos
      if (e.shiftKey && (e.key === "A" || e.key === "a")) {
        e.preventDefault();
        selectAllEmails();
        return;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [showCompose, showGlobalShortcutsModal, del, patch]);

  return (
    <div className="h-full w-full flex flex-col overflow-hidden">
      <OfflineBanner
        isOnline={isOnline}
        outboxCount={outboxCount}
        isSyncing={isSyncing}
        onSyncNow={() => processOutbox(token)}
      />
      <MailLayout
        onComposeOpen={() => {
          setReplyTo(undefined);
          setShowCompose(true);
        }}
        onReply={handleReply}
        onReplyAll={handleReplyAll}
        onForward={handleForward}
        onEditDraft={handleEditDraft}
      />
      <ComposeModal
        isOpen={showCompose}
        onClose={() => {
          setShowCompose(false);
          setReplyTo(undefined);
        }}
        onSent={() => refreshFolders(token)}
        replyTo={replyTo}
      />
      <KeyboardShortcutsModal
        isOpen={showGlobalShortcutsModal}
        onClose={() => setShowGlobalShortcutsModal(false)}
      />
    </div>
  );
}
